import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Check, ChevronDown, Lightbulb, X } from 'lucide-react';
import { useState } from 'react';

interface ProviderRule {
  id: string;
  ruleType: 'ALLOW' | 'DENY';
  entityType: 'CONDITION' | 'INTENT' | 'PROCEDURE';
  canonicalTagLabel: string;
}

interface VisualRuleSimulationProps {
  rules: ProviderRule[];
  providerName: string;
}

export function VisualRuleSimulation({ rules, providerName }: VisualRuleSimulationProps) {
  const [isOpen, setIsOpen] = useState(false);

  const allowRules = rules.filter((r) => r.ruleType === 'ALLOW');
  const denyRules = rules.filter((r) => r.ruleType === 'DENY');

  // Group by entity type
  const groupByEntityType = (rulesList: ProviderRule[]) => {
    const grouped: Record<string, string[]> = {
      CONDITION: [],
      INTENT: [],
      PROCEDURE: [],
    };
    for (const rule of rulesList) {
      const entityGroup = grouped[rule.entityType];
      if (entityGroup) {
        entityGroup.push(rule.canonicalTagLabel);
      }
    }
    return grouped;
  };

  const allowByType = groupByEntityType(allowRules);
  const denyByType = groupByEntityType(denyRules);

  if (rules.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-base">Patient Visibility Simulation</CardTitle>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-gray-500 transition-transform ${
                  isOpen ? 'transform rotate-180' : ''
                }`}
              />
            </div>
            <CardDescription className="text-left">
              Preview what patient types {providerName} can see based on current rules
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {/* ALLOW Section */}
            {allowRules.length > 0 && (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold text-green-900">
                        ✅ ALLOW rules are restrictive
                      </h4>
                      <p className="text-xs text-green-800">
                        This provider can <strong>ONLY</strong> see patients tagged with one of
                        these. All other patients will be hidden.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="ml-6 space-y-2">
                  {Object.entries(allowByType).map(([type, tags]) => {
                    if (tags.length === 0) return null;
                    return (
                      <div key={type} className="space-y-1">
                        <Badge variant="outline" className="text-xs">
                          {type}
                        </Badge>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                          {tags.map((tag) => (
                            <li key={`allow-${type}-${tag}`}>{tag}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* DENY Section */}
            {denyRules.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <X className="h-4 w-4 text-red-600" />
                  <h4 className="text-sm font-semibold text-red-900">
                    Cannot see patients with these tags:
                  </h4>
                </div>
                <div className="ml-6 space-y-2">
                  {Object.entries(denyByType).map(([type, tags]) => {
                    if (tags.length === 0) return null;
                    return (
                      <div key={type} className="space-y-1">
                        <Badge variant="outline" className="text-xs">
                          {type}
                        </Badge>
                        <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                          {tags.map((tag) => (
                            <li key={`deny-${type}-${tag}`}>{tag}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No ALLOW rules warning */}
            {allowRules.length === 0 && denyRules.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-amber-900">
                      No ALLOW rules = sees all patients
                    </h4>
                    <p className="text-xs text-amber-800">
                      Without ALLOW rules, this provider can see <strong>ALL</strong> patients,
                      except those with DENY tags.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No rules at all */}
            {allowRules.length === 0 && denyRules.length === 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-sm text-gray-600">
                  <strong>No qualification rules configured.</strong> This provider can see all
                  patients (subject to age restrictions configured separately).
                </p>
              </div>
            )}

            {/* Evaluation Logic Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
              <p className="font-medium mb-1">Rule Evaluation Order:</p>
              <ul className="list-decimal list-inside space-y-1 text-blue-800">
                <li>
                  <strong>DENY rules</strong> are checked first - if patient has a DENY tag, they're
                  immediately excluded
                </li>
                <li>
                  <strong>ALLOW rules</strong> create a whitelist - if ANY ALLOW rules exist,
                  patient MUST have one of those tags
                </li>
                <li>
                  <strong>No rules</strong> means provider sees all patients (except age
                  restrictions)
                </li>
              </ul>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
